import Slider from "react-slick";
import { useRef, useState } from "react";
import styles from "./index.module.css";
import Left from "./left";
import Right from "./right";
import PopupModal from "../PopupModal";

const padNumber = (value) => String(value).padStart(2, "0");

// The breakpoints mirror the ones in index.module.css, where the header arrows
// give way to the nav under the slider.
const getSlidesToShow = () => {
  if (window.innerWidth <= 900) return 1;
  if (window.innerWidth <= 1200) return 2;
  return 3;
};

const responsive = [
  { breakpoint: 900, settings: { slidesToShow: 1 } },
  { breakpoint: 1200, settings: { slidesToShow: 2 } },
];

function ArrowButton({ direction, onClick }) {
  const isPrev = direction === "prev";

  return (
    <button
      type="button"
      aria-label={isPrev ? "Попередній слайд" : "Наступний слайд"}
      onClick={onClick}
      className={styles.controlButton}
    >
      {isPrev ? <Left /> : <Right />}
    </button>
  );
}

/**
 * Carousel of cards with a shared look: heading row, arrows (a counter row
 * below the slider once they are hidden on narrow screens) and a modal that
 * shows a card in full. Cards clamp their own body with <Clampable>, whose
 * "більше" button is wired to this modal through renderCard's onShowMore.
 *
 * @param {string} title
 * @param {React.ReactNode} [controls] — extra header controls, left of the arrows
 * @param {string} [headClassName] — to size the heading and its spacing
 * @param {Array} items
 * @param {(item, api: { onShowMore: () => void }) => React.ReactNode} renderCard
 * @param {(item) => React.ReactNode} [renderExpanded] — the card inside the modal
 * @param {object} [settings] — react-slick overrides
 * @param {*} [resetKey] — changing it rebuilds the slider and rewinds it
 * @param {boolean} [headerArrowsOnMobile] — keep the arrows in the heading on
 *   narrow screens instead of moving them to the counter row below the slider
 */
export default function CardSlider({
  title,
  controls,
  headClassName,
  items,
  renderCard,
  renderExpanded,
  settings,
  resetKey,
  headerArrowsOnMobile = false,
}) {
  const sliderRef = useRef(null);
  const [currentItem, setCurrentItem] = useState(0);
  const [expandedItem, setExpandedItem] = useState(null);
  const [renderedKey, setRenderedKey] = useState(resetKey);

  if (renderedKey !== resetKey) {
    setRenderedKey(resetKey);
    setCurrentItem(0);
    setExpandedItem(null);
  }

  const prev = () => sliderRef.current?.slickPrev();
  const next = () => sliderRef.current?.slickNext();

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: getSlidesToShow(),
    slidesToScroll: 1,
    arrows: false,
    responsive,
    ...settings,
    afterChange: setCurrentItem,
  };

  return (
    <>
      <div
        className={[
          styles.head,
          headerArrowsOnMobile && styles.headArrows,
          headClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <h2>{title}</h2>

        <div className={styles.headControls}>
          {controls}

          <div className={styles.arrows}>
            <ArrowButton direction="prev" onClick={prev} />
            <ArrowButton direction="next" onClick={next} />
          </div>
        </div>
      </div>

      <div className={styles.slider}>
        <Slider key={resetKey} ref={sliderRef} {...sliderSettings}>
          {items.map((item, index) => (
            <div className={styles.pad} key={index}>
              {renderCard(item, { onShowMore: () => setExpandedItem(item) })}
            </div>
          ))}
        </Slider>
      </div>

      {!headerArrowsOnMobile && (
        <div className={styles.mobileNav}>
          <ArrowButton direction="prev" onClick={prev} />
          <p className={styles.counter}>
            {padNumber(currentItem + 1)}
            <span> / {padNumber(items.length)}</span>
          </p>
          <ArrowButton direction="next" onClick={next} />
        </div>
      )}

      {expandedItem && renderExpanded && (
        <PopupModal
          close={() => setExpandedItem(null)}
          contentClassName={styles.modalContent}
        >
          <div className={styles.modalInner}>
            {renderExpanded(expandedItem)}
          </div>
        </PopupModal>
      )}
    </>
  );
}
